/**
 * What the platform will enforce at run time, said in the Builder first (ADR-0087).
 *
 * The tool server refuses a call when the agent's data level is below the data the action
 * returns, when the agent's risk tier is below the action's, or when the person running the
 * agent lacks a role the action needs; the model gateway never sends data to a model above
 * that model's level. The control-plane catalogue publishes those facts per action
 * (`risk_tier`, `classification`, `required_roles`, `amount_field`) and the gateway per model
 * (`max_classification`, `local_max_classification`). These helpers turn them into checks with
 * a one-click fix where one is safe. OPA stays authoritative: these are early warnings only.
 *
 * Facts an older control plane or gateway does not publish are "unknown" and never reported.
 */
import type { AliasList, Classification, JsonSchemaLite, RegistryTool, RiskTier } from "../types";
import type { PlainIssue } from "./plain";
import type { AgentSpec } from "./types";

/** Agents made without code (the guided builder's no-code profile) go up to this risk tier. */
export const NOCODE_MAX_TIER: RiskTier = 2;

const RANK: Record<string, number> = { public: 0, internal: 1, confidential: 2, restricted: 3 };
export const levelRank = (c: string | null | undefined): number => RANK[c ?? "internal"] ?? 1;

export type ToolAction = RegistryTool["actions"][number];
export type ModelFacts = Pick<AliasList["data"][number], "id" | "hosting" | "max_classification" | "local_max_classification"> & Partial<Pick<AliasList["data"][number], "capabilities" | "description" | "private_model">>;

export function findAction(tools: readonly RegistryTool[], ref: string, action: string): { tool: RegistryTool | undefined; action: ToolAction | undefined } {
  const tool = tools.find((t) => t.ref === ref);
  return { tool, action: tool?.actions.find((a) => a.name === action) };
}

/** The risk tier an action needs, or null when the catalogue does not say. */
export const actionTier = (a: ToolAction | undefined): RiskTier | null => a?.risk_tier ?? null;

/** The most sensitive data an action returns, or null when the catalogue does not say. */
export const actionLevel = (a: ToolAction | undefined): Classification | null => a?.classification ?? null;

/** The tool/action pairs an agent may call: its grants and its system steps. */
export function usedActions(spec: AgentSpec): { ref: string; action: string; node?: string }[] {
  const out: { ref: string; action: string; node?: string }[] = [];
  for (const n of spec.spec.graph.nodes) if (n.type === "tool" && n.tool && n.action) out.push({ ref: n.tool, action: n.action, node: n.id });
  for (const g of spec.spec.tools ?? []) for (const a of g.actions) if (!out.some((o) => o.ref === g.ref && o.action === a)) out.push({ ref: g.ref, action: a });
  return out;
}

/** The highest risk tier among actions (null when none is known). */
export function neededTier(pairs: readonly { ref: string; action: string }[], tools: readonly RegistryTool[]): { tier: RiskTier; ref: string; action: string } | null {
  let best: { tier: RiskTier; ref: string; action: string } | null = null;
  for (const p of pairs) {
    const tier = actionTier(findAction(tools, p.ref, p.action).action);
    if (tier !== null && (!best || tier > best.tier)) best = { tier, ref: p.ref, action: p.action };
  }
  return best;
}

/**
 * Whether a model may see data at `level`: `all` (any of its models), `local` (only its models
 * on your own servers: smaller ones, so simpler answers), `no`, or `unknown` (older gateway).
 */
export type ModelReach = "all" | "local" | "no" | "unknown";
export function modelReach(m: ModelFacts | undefined, level: Classification): ModelReach {
  if (!m || !m.max_classification) return "unknown";
  const want = levelRank(level);
  const local = m.local_max_classification ? levelRank(m.local_max_classification) >= want : false;
  // Platform rule: Confidential and Restricted data never go to online services.
  if (want >= 2 && m.hosting !== undefined && m.hosting !== "local") return local ? "local" : "no";
  if (levelRank(m.max_classification) >= want) return "all";
  return local ? "local" : "no";
}

/** The most sensitive data a model can see at all (through its local models if need be). */
export function modelTop(m: ModelFacts): Classification | null {
  const top = [m.max_classification, m.local_max_classification].filter((x): x is Classification => !!x);
  if (!top.length) return null;
  const best = top.reduce((a, b) => (levelRank(b) > levelRank(a) ? b : a));
  // Online models never see more than Internal, whatever their card says.
  return m.hosting === "online" && levelRank(best) > 1 ? "internal" : best;
}

/** Chat models agents can talk through (not search, speech or the platform's own checks). */
const INTERNAL_ALIAS = /^(decide|guardrails|docintel)-/;
export const chatModels = <M extends { id: string; capabilities?: string[] }>(models: readonly M[]): M[] =>
  models.filter((m) => (m.capabilities ?? ["chat"]).includes("chat") && !INTERNAL_ALIAS.test(m.id));

// ---------------------------------------------------------------- schema helpers

/** The type of a schema, looking through `anyOf: [{type: X}, {type: null}]` (nullable fields). */
export function schemaKind(s: JsonSchemaLite | undefined): JsonSchemaLite["type"] | undefined {
  if (!s) return undefined;
  if (s.type) return s.type;
  const alt = (s.anyOf ?? []).find((x): x is JsonSchemaLite => x.type !== "null");
  return alt ? schemaKind(alt) : s.properties ? "object" : undefined;
}

/** The non-null branch of a nullable schema (or the schema itself). */
export function schemaOf(s: JsonSchemaLite | undefined): JsonSchemaLite | undefined {
  if (!s || s.type || !s.anyOf) return s;
  return s.anyOf.find((x): x is JsonSchemaLite => x.type !== "null") ?? s;
}

/** Number fields of an action's arguments with dotted paths, nested objects included (`body.amount`). */
export function numberFields(schema: JsonSchemaLite | undefined, prefix = "", depth = 0): { path: string; name: string; title?: string }[] {
  const out: { path: string; name: string; title?: string }[] = [];
  const s = schemaOf(schema);
  if (!s?.properties || depth > 4) return out;
  for (const [name, raw] of Object.entries(s.properties)) {
    const f = schemaOf(raw);
    const path = prefix ? `${prefix}.${name}` : name;
    const kind = schemaKind(f);
    if (kind === "number" || kind === "integer") out.push({ path, name, ...(f?.title ? { title: f.title } : {}) });
    else if (kind === "object") out.push(...numberFields(f, path, depth + 1));
  }
  return out;
}

// ---------------------------------------------------------------- checks for the graph editor

export interface AccessOptions {
  /** The roles of the person building (from the sign-in); omit to skip role checks. */
  roles?: readonly string[];
  /** Model facts from the gateway; omit to skip model checks. */
  models?: readonly ModelFacts[];
}

const toolName = (tools: readonly RegistryTool[], ref: string, lang: "ar" | "en") => {
  const t = tools.find((x) => x.ref === ref);
  return t ? (lang === "ar" ? t.name.ar || t.name.en : t.name.en || t.name.ar) : ref;
};

/**
 * Problems the platform would refuse at run time, in plain words (codes under
 * `builder:plain.issue`): a system holding more sensitive data than the agent may handle
 * (`dataLevel`, fix: raise the level), an action above the agent's risk tier (`riskTier`, fix:
 * raise it; `riskTierNocode` when the guided builder's limit is in the way), a role the person
 * lacks (`roleNeeded`), and a model that may not see the agent's data (`modelLevel`).
 */
export function accessIssues(spec: AgentSpec, tools: readonly RegistryTool[], opts: AccessOptions = {}, lang: "ar" | "en" = "en"): PlainIssue[] {
  const out: PlainIssue[] = [];
  const s = spec.spec;
  const agentLevel = (s.permissions?.max_classification as Classification | undefined) ?? "internal";
  const nodes = s.graph.nodes;
  const stepName = (id: string | undefined) => {
    const n = id ? nodes.find((x) => x.id === id) : undefined;
    const l = n?.label ? (lang === "ar" ? n.label.ar || n.label.en : n.label.en || n.label.ar) : "";
    return l || id || "";
  };
  const pairs = usedActions(spec);
  // Data level: one line per system, at its most sensitive action.
  const levels = new Map<string, Classification>();
  for (const p of pairs) {
    const level = actionLevel(findAction(tools, p.ref, p.action).action);
    if (level && levelRank(level) > levelRank(agentLevel) && levelRank(level) > levelRank(levels.get(p.ref))) levels.set(p.ref, level);
  }
  for (const [ref, level] of levels) {
    out.push({ code: "dataLevel", params: { tool: toolName(tools, ref, lang), level, agentLevel }, field: "level", fix: { kind: "level", level }, raw: `tool ${ref} returns ${level} data; agent max_classification is ${agentLevel}` });
  }
  // Risk tier.
  const need = neededTier(pairs, tools);
  if (need && need.tier > s.risk_tier) {
    const node = pairs.find((p) => p.ref === need.ref && p.action === need.action)?.node;
    const step = node ? stepName(node) : toolName(tools, need.ref, lang);
    const raw = `action ${need.ref}.${need.action} needs risk tier ${need.tier}; agent risk_tier is ${s.risk_tier}`;
    const nocode = s.builder_profile === "nocode" && need.tier > NOCODE_MAX_TIER;
    out.push({ code: nocode ? "riskTierNocode" : "riskTier", params: { step, tier: String(need.tier), agentTier: String(s.risk_tier) }, field: "risk", ...(node ? { node } : {}), ...(nocode ? {} : { fix: { kind: "tier" as const, tier: need.tier } }), raw });
  }
  // Roles the person running it must hold (they try it as themselves).
  if (opts.roles) {
    const have = new Set(opts.roles);
    const seen = new Set<string>();
    for (const p of pairs) {
      const missing = (findAction(tools, p.ref, p.action).action?.required_roles ?? []).filter((r) => !have.has(r));
      const key = `${p.ref}.${p.action}`;
      if (!missing.length || seen.has(key)) continue;
      seen.add(key);
      out.push({ code: "roleNeeded", params: { step: p.node ? stepName(p.node) : toolName(tools, p.ref, lang), roles: missing.join(", ") }, ...(p.node ? { node: p.node } : {}), raw: `action ${key} requires roles ${missing.join(", ")}` });
    }
  }
  // Models: the agent's and each step's own.
  if (opts.models) {
    const check = (alias: string | undefined, node?: string) => {
      if (!alias) return;
      const m = opts.models?.find((x) => x.id === alias);
      if (m && modelReach(m, agentLevel) === "no") {
        out.push({ code: node ? "modelLevelStep" : "modelLevel", params: { step: stepName(node), model: alias, level: agentLevel, top: modelTop(m) ?? "internal" }, ...(node ? { node } : { field: "model" }), raw: `model ${alias} may not receive ${agentLevel} data` });
      }
    };
    check(s.model.alias);
    for (const n of nodes) {
      const alias = (n.model as { alias?: string } | undefined)?.alias;
      if (n.type === "llm" && alias && alias !== s.model.alias) check(alias, n.id);
    }
  }
  return out;
}

